import React, { useState, useEffect, useRef } from 'react';
import { makerVariant } from './variant-resolver.tsx';
const NewHero: React.FC = () => {
  const slides = [{
    prompt: "Make a winter collection landing page",
    type: "image",
    src: "https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_1366,f_auto,q_auto/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Forg_2REc1tnPEsPISxOixJ2l0CV1dpe%2Fcmkgdrd82009cwf0ybu6o3ras%2Fmessages%2Fcollection-jrykgbvrs8p.webp"
  }, {
    prompt: "Make an A+ content for my product page",
    type: "image",
    src: "https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_1366,f_auto,q_auto/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Forg_2REc1tnPEsPISxOixJ2l0CV1dpe%2Fcmkgdrd82009cwf0ybu6o3ras%2Fmessages%2Fpdp-gmos1livnft.webp"
  }, {
    prompt: "Make a launch count-down timer",
    type: "image",
    src: "https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_1366,f_auto,q_auto/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Forg_2REc1tnPEsPISxOixJ2l0CV1dpe%2Fcmkgdrd82009cwf0ybu6o3ras%2Fmessages%2Ftimer-zm3sajwb6zk.webp"
  }, {
    prompt: "Make an interactive quiz",
    type: "image",
    src: "https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_1366,f_auto,q_auto/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Forg_2REc1tnPEsPISxOixJ2l0CV1dpe%2Fcmkgdrd82009cwf0ybu6o3ras%2Fmessages%2Fquiz-k72mpen6w6f.webp"
  }, {
    prompt: "Make a bento-style hero section",
    type: "image",
    src: "https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_1366,f_auto,q_auto/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Forg_2REc1tnPEsPISxOixJ2l0CV1dpe%2Fcmkgdrd82009cwf0ybu6o3ras%2Fmessages%2Fbento-hero-3e09b6xqjz2.webp"
  }, {
    prompt: "Make a visual navigation for mobile",
    type: "image",
    src: "https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_1366,f_auto,q_auto/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Forg_2REc1tnPEsPISxOixJ2l0CV1dpe%2Fcmkgdrd82009cwf0ybu6o3ras%2Fmessages%2Fmenu-e9q4il5p01q.webp"
  }];
  const prompts = slides.map(slide => slide.prompt);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showCursor, setShowCursor] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const typingTimeoutRef = useRef<number | null>(null);
  const slideTimeoutRef = useRef<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // Intersection Observer to pause animations when out of viewport
  useEffect(() => {
    const currentRef = sectionRef.current;
    if (!currentRef) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, {
      threshold: 0.1
    });
    observer.observe(currentRef);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Check for prefers-reduced-motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Typing animation effect
  useEffect(() => {
    if (!isVisible || isPaused || prefersReducedMotion) return;
    const currentPrompt = prompts[currentIndex];
    if (isTyping) {
      if (displayedText.length < currentPrompt.length) {
        typingTimeoutRef.current = setTimeout(() => {
          setDisplayedText(currentPrompt.slice(0, displayedText.length + 1));
        }, 50);
      } else {
        // Finished typing, wait 3 seconds before fading out
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
        }, 3000);
      }
    } else {
      // Fade out and move to next slide
      typingTimeoutRef.current = setTimeout(() => {
        setDisplayedText('');
        setCurrentIndex(prev => (prev + 1) % prompts.length);
        setIsTyping(true);
      }, 300);
    }
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [displayedText, isTyping, currentIndex, isVisible, isPaused, prefersReducedMotion, prompts]);

  // Auto-resume after manual navigation
  useEffect(() => {
    if (isPaused) {
      slideTimeoutRef.current = setTimeout(() => {
        setIsPaused(false);
      }, 4000);
    }
    return () => {
      if (slideTimeoutRef.current) {
        clearTimeout(slideTimeoutRef.current);
      }
    };
  }, [isPaused]);
  const handlePrevious = () => {
    const newIndex = (currentIndex - 1 + prompts.length) % prompts.length;
    setCurrentIndex(newIndex);
    setDisplayedText(prompts[newIndex]);
    setIsTyping(false);
    setIsPaused(true);
    setTimeout(() => {
      setIsTyping(true);
    }, 100);
  };
  const handleNext = () => {
    const newIndex = (currentIndex + 1) % prompts.length;
    setCurrentIndex(newIndex);
    setDisplayedText(prompts[newIndex]);
    setIsTyping(false);
    setIsPaused(true);
    setTimeout(() => {
      setIsTyping(true);
    }, 100);
  };
  const isCentered = String(makerVariant('heroLayout', 'split')) === 'centered';
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const isDesktop = windowWidth >= 1024;

  // Centered carousel — desktop: fixed px sizes (active 600px, peek 300px);
  // mobile: % based (80% width for all cards).
  const getCenteredSlideStyle = (index: number): React.CSSProperties => {
    const total = slides.length;
    const diff = (index - currentIndex + total) % total;
    if (isDesktop) {
      const activeW = 600;
      const peekW = 300;
      const gap = 24; // px between active and peek
      const centerLeft = (windowWidth - activeW) / 2;
      let left: number;
      let width: number;
      let zIndex: number;
      if (diff === 0) {
        left = centerLeft;
        width = activeW;
        zIndex = 20;
      } else if (diff === 1) {
        left = centerLeft + activeW + gap;
        width = peekW;
        zIndex = 10;
      } else if (diff === total - 1) {
        left = centerLeft - peekW - gap;
        width = peekW;
        zIndex = 10;
      } else if (diff >= 2 && diff <= Math.floor(total / 2)) {
        left = centerLeft + activeW + peekW + gap * 2;
        width = peekW;
        zIndex = 0;
      } else {
        left = centerLeft - peekW * 2 - gap * 2;
        width = peekW;
        zIndex = 0;
      }
      return {
        position: 'absolute' as const,
        top: 0,
        left: `${left}px`,
        width: `${width}px`,
        height: '100%',
        zIndex,
        transition: 'left 600ms cubic-bezier(0.4,0,0.2,1), width 600ms cubic-bezier(0.4,0,0.2,1)',
        willChange: 'left, width'
      };
    }

    // Mobile: % based, same width for all cards
    const cardWidth = 80;
    const gap = 2; // % of container
    const centerLeft = (100 - cardWidth) / 2;
    let left: number;
    let zIndex: number;
    if (diff === 0) {
      left = centerLeft;
      zIndex = 20;
    } else if (diff === 1) {
      left = centerLeft + cardWidth + gap;
      zIndex = 10;
    } else if (diff === total - 1) {
      left = centerLeft - cardWidth - gap;
      zIndex = 10;
    } else if (diff >= 2 && diff <= Math.floor(total / 2)) {
      left = centerLeft + (cardWidth + gap) * 2;
      zIndex = 0;
    } else {
      left = centerLeft - (cardWidth + gap) * 2;
      zIndex = 0;
    }
    return {
      position: 'absolute' as const,
      top: 0,
      left: `${left}%`,
      width: `${cardWidth}%`,
      height: '100%',
      zIndex,
      transition: 'left 600ms cubic-bezier(0.4,0,0.2,1)',
      willChange: 'left'
    };
  };
  const getSlidePosition = (index: number) => {
    const total = prompts.length;
    const diff = (index - currentIndex + total) % total;
    if (diff === 0) return 'translate-x-0 scale-100 opacity-100 z-20';
    if (diff === 1) return 'translate-x-[492px] md:translate-x-[592px] lg:translate-x-[652px] 2xl:translate-x-[556px] scale-100 2xl:scale-[0.7] z-10';
    return 'translate-x-[900px] md:translate-x-[1100px] lg:translate-x-[1200px] scale-100 2xl:scale-[0.7] z-0';
  };
  const arrowSrc = "https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_48,f_svg,q_auto/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Forg_2REc1tnPEsPISxOixJ2l0CV1dpe%2Fk25p2kmk28854tt5psupfrk8%2Fmessages%2Farrow-9g76mlp1op6.svg";
  if (isCentered) {
    return <section ref={sectionRef} data-name="Hero Section" className="w-full bg-cream pt-12 lg:pt-24 pb-12 lg:pb-20 overflow-x-hidden">
        {/* Centered copy block */}
        <div className="max-w-[720px] mx-auto px-4 md:px-6 text-center mb-10 lg:mb-14">
          <h1 data-name="Main Headline" className="text-[#111111] mb-6 lg:mb-8">
            <span className="[font-family:Aeonik-Pro,sans-serif] font-normal text-[clamp(40px,calc(23px+5.36vw),64px)] md:text-[64px] lg:text-[clamp(64px,calc(24.58px+3.85vw),80px)] leading-none block">
              {makerVariant('headlineLine1', 'Make anything')}
            </span>
            <span className="[font-family:Ogg,serif] font-normal text-[clamp(40px,calc(23px+5.36vw),64px)] md:text-[64px] lg:text-[clamp(64px,calc(24.58px+3.85vw),80px)] leading-none block">
              {makerVariant('headlineLine2', 'for your website')}
            </span>
          </h1>
          <p data-name="Description" className="[font-family:Aeonik-Pro,sans-serif] text-[16px] lg:text-[20px] leading-[1.4] text-primary mb-8 lg:mb-10">
            Describe what you want. Maker designs it, codes it, and serves it live on your site. No developers. No agencies. No rebuild.
          </p>
          {/* Inline prompt + CTA */}
          <div className="flex gap-3 items-stretch">
            <div data-name="Prompt Box" role="region" aria-live="polite" aria-atomic="true" className="flex-1 bg-[#F3F1EC] rounded-2xl px-5 py-4 border border-solid border-[rgba(17,17,17,0.06)] text-left flex items-center min-w-0">
              <p className="[font-family:Aeonik-Pro,sans-serif] text-[16px] lg:text-[18px] leading-[1.4] text-primary truncate">
                <span className={`transition-opacity duration-300 ${isTyping ? 'opacity-100' : 'opacity-0'}`}>
                  {displayedText}
                </span>
                {!prefersReducedMotion && isTyping && displayedText.length > 0 && <span className="inline-block w-[2px] h-5 bg-[#111111] ml-1 cursor-blink align-middle" />}
              </p>
            </div>
            <a href="https://ai.maker.co/waitlist" target="_blank" rel="noopener noreferrer" data-name="Waitlist Button" data-tracking-id="link-7413a8f205096b8a" className="flex-shrink-0 bg-primary text-white [font-family:Aeonik-Pro,sans-serif] font-medium text-[16px] leading-6 px-6 py-4 rounded-xl hover:bg-accent transition-colors ease-in-out duration-300 inline-flex items-center gap-2 tracking-[0.03em] whitespace-nowrap">
              {makerVariant('ctaCopy', 'Get Started')}
            </a>
          </div>
        </div>

        {/* Full-width centered carousel — desktop: 450px tall (600×0.75); mobile: 60% padding-top (80%×0.75) */}
        <div className="relative w-full overflow-hidden" style={isDesktop ? {
        height: '450px'
      } : {
        paddingTop: '60%'
      }}>
          <div className="absolute inset-0">
            {slides.map((slide, index) => {
            const isInitialSlide = index === 0;
            const imgProps = isInitialSlide ? {
              fetchPriority: 'high' as const
            } : {
              loading: 'lazy' as const
            };
            return <div key={index} data-name={`Carousel Card ${index + 1}`} style={getCenteredSlideStyle(index)}>
                  <div className="w-full h-full rounded-2xl overflow-hidden">
                    <img src={slide.src} alt={slide.prompt} {...imgProps} className="w-full h-full object-cover" />
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Navigation arrows centered */}
        <div data-name="Navigation Arrows" className="flex items-center gap-6 mt-8 justify-center">
          <button onClick={handlePrevious} aria-label="Previous slide" className="hover:scale-110 transition-transform duration-200" data-tracking-id="btn-51e029e68817cc66">
            <img src={arrowSrc} alt="Previous" className="w-6 h-6 rotate-180" />
          </button>
          <button onClick={handleNext} aria-label="Next slide" className="hover:scale-110 transition-transform duration-200" data-tracking-id="btn-97b77dd8927c568c">
            <img src={arrowSrc} alt="Next" className="w-6 h-6" />
          </button>
        </div>
      </section>;
  }
  return <section ref={sectionRef} data-name="Hero Section" className="w-full bg-cream py-12 lg:py-40 mt-0 mb-0 ml-0 mr-0 overflow-x-hidden pl-0 pr-0">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-16 items-center">
          {/* Left Column */}
          <div data-name="Left Column Content" className="space-y-4 lg:space-y-[36px]">
            <h1 data-name="Main Headline" className="text-[#111111]">
              <span className="[font-family:Aeonik-Pro,sans-serif] font-normal text-[clamp(40px,calc(23px+5.36vw),64px)] md:text-[64px] lg:text-[clamp(64px,calc(24.58px+3.85vw),80px)] leading-none block">
                {makerVariant('headlineLine1', 'Make anything')}
              </span>
              <span className="[font-family:Ogg,serif] font-normal text-[clamp(40px,calc(23px+5.36vw),64px)] md:text-[64px] lg:text-[clamp(64px,calc(24.58px+3.85vw),80px)] leading-none block">
                {makerVariant('headlineLine2', 'for your website')}
              </span>
            </h1>
            <p data-name="Description Mobile" className="lg:hidden [font-family:Aeonik-Pro,sans-serif] text-[16px] leading-[1.4] text-[rgb(17,17,17)] max-w-lg mt-0 mb-0 ml-0 mr-0 pt-0 pb-0 pl-0 pr-0">
              Describe what you want. Maker designs it, codes it, and serves it live on your site. No developers. No agencies. No rebuild.
            </p>
            <div data-name="Prompt Box" role="region" aria-live="polite" aria-atomic="true" className="bg-[#F3F1EC] rounded-2xl p-4 h-[180px] md:h-[200px] lg:h-[220px] relative border border-solid border-[rgba(17,17,17,0.06)]">
              <div className="pr-4 pb-16">
                <p className="[font-family:Aeonik-Pro,sans-serif] text-xl leading-7 md:text-[2rem] md:leading-[1.4] text-primary">
                  <span className={`transition-opacity duration-300 ${isTyping ? 'opacity-100' : 'opacity-0'}`}>
                    {displayedText}
                  </span>
                  {!prefersReducedMotion && isTyping && displayedText.length > 0 && <span className="inline-block w-[2px] h-6 bg-[#111111] ml-1 cursor-blink align-middle"></span>}
                </p>
              </div>
              <a href="https://ai.maker.co/waitlist" target="_blank" rel="noopener noreferrer" data-name="Waitlist Button" data-tracking-id="link-7413a8f205096b8a" className="duration absolute bottom-4 right-4 bg-primary text-white [font-family:Aeonik-Pro,sans-serif] font-medium text-[16px] leading-6 px-6 py-3 rounded-xl hover:bg-accent transition-colors ease-in-out duration ease-in-out duration-300 inline-flex items-center gap-2 mt-0 mb-0 ml-0 mr-0 tracking-[0.03em]">{makerVariant('ctaCopy', 'Get Started')}</a>
            </div>
            <p data-name="Description" className="hidden lg:block [font-family:Aeonik-Pro,sans-serif] text-[16px] lg:text-[20px] leading-[1.4] text-primary">Describe what you want. Maker designs it, codes it, and serves it live on your site. No developers. No agencies. No rebuild.</p>
          </div>
          {/* Right Column - Carousel */}
          <div data-name="Carousel Section" className="relative">
            <div className="relative w-full max-w-[480px] md:max-w-[580px] lg:max-w-[640px] aspect-[4/3]">
              {slides.map((slide, index) => {
              const isInitialSlide = index === 0;
              const imgProps = isInitialSlide ? {
                fetchPriority: 'high' as const
              } : {
                loading: 'lazy' as const
              };
              return <div key={index} data-name={`Carousel Card ${index + 1}`} className={`absolute left-0 top-0 w-full aspect-[4/3] transition-all duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${getSlidePosition(index)}`} style={{
                willChange: 'transform, opacity'
              }}>
                  <div className="w-full h-full rounded-lg overflow-hidden rounded-tl-2xl rounded-tr-2xl rounded-br-2xl rounded-bl-2xl">
                    <img src={slide.src} alt={slide.prompt} {...imgProps} className="w-full h-full object-cover" />
                  </div>
                </div>;
            })}
            </div>
            <div data-name="Navigation Arrows" className="flex items-center gap-6 mt-8 justify-center lg:justify-end">
              <button onClick={handlePrevious} aria-label="Previous slide" className="hover:scale-110 transition-transform duration-200" data-tracking-id="btn-51e029e68817cc66">
                <img src={arrowSrc} alt="Previous" className="w-6 h-6 rotate-180" />
              </button>
              <button onClick={handleNext} aria-label="Next slide" className="hover:scale-110 transition-transform duration-200" data-tracking-id="btn-97b77dd8927c568c">
                <img src={arrowSrc} alt="Next" className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default NewHero;